import { Outlet, createBrowserRouter, type RouteObject } from 'react-router';
import { homeFor, SessionProvider, useSession } from '../shared/auth/session.tsx';
import type { RouteHandle } from './current-route.ts';
import { RequireSession, RouteGate } from './guards.tsx';
import { NotFoundPage } from './pages/SystemPages.tsx';
import { RouteErrorBoundary } from './RouteErrorBoundary.tsx';
import { routes, type RouteDef } from './routes.config.ts';

function SessionRoot() {
  return (
    <SessionProvider>
      <Outlet />
    </SessionProvider>
  );
}

function ShellNotFound() {
  const { identity } = useSession();
  return <NotFoundPage home={homeFor(identity)} />;
}

function toRouteObject(route: RouteDef): RouteObject {
  return {
    path: route.path,
    handle: { route } satisfies RouteHandle,
    lazy: async () => {
      const { Component } = await route.lazy();
      return {
        Component: () => (
          <RouteGate route={route}>
            <Component />
          </RouteGate>
        ),
      };
    },
  };
}

/**
 * Router (08 §11.3): korunan rotalar `routes.config`'ten üretilir ve platform kabuğunda açılır; oturum açma ve davet
 * kabulü oturumsuzdur. Bilinmeyen adres kabukta 404'tür. Tasarım sistemi vitrini backoffice'te yoktur (panelde).
 */
export function buildRoutes(): RouteObject[] {
  return [
    {
      element: <SessionRoot />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          lazy: async () => ({ Component: (await import('../features/auth/AuthLayout.tsx')).AuthLayout }),
          children: [
            { path: '/login', lazy: async () => ({ Component: (await import('../features/auth/LoginPage.tsx')).LoginPage }) },
            { path: '/activate/:token', lazy: async () => ({ Component: (await import('../features/auth/ActivatePage.tsx')).ActivatePage }) },
          ],
        },
        {
          element: <RequireSession />,
          children: [
            {
              lazy: async () => ({ Component: (await import('./shell/PlatformShell.tsx')).PlatformShell }),
              children: [...routes.map(toRouteObject), { path: '*', element: <ShellNotFound /> }],
            },
          ],
        },
      ],
    },
  ];
}

export function createAppRouter() {
  return createBrowserRouter(buildRoutes());
}
